import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

import { fetchWithDefaults } from '@/lib/http/client';
import type { CrmObject } from '@/types/crm';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { EVENT_GROUP_TO_TYPES } from './constants';
import type { LinkedHighlightResult } from './types';

interface ViewStateEvent {
  /**
   * The entity the event was recorded on. For header metrics this is usually
   * an ASSOCIATED entity — a contact's "Last emailed" comes from an
   * `ahoy_email_sent` event on the `ahoy_email_thread`, not on the contact —
   * which makes it exactly the record the tile should open.
   */
  entity_id: string;
  /** Empty string when the entity wasn't in the response's lookup table. */
  entity_type_name?: string;
  event_type_name: string;
  occurred_at: string;
}

/** The single event a `last_event` highlight resolved to. */
interface LastEventMatch {
  occurredAt: string;
  entityId: string;
  entityTypeName?: string;
}

function toMatch(event: ViewStateEvent | undefined): LastEventMatch | null {
  if (!event) {
    return null;
  }
  return {
    occurredAt: event.occurred_at,
    entityId: event.entity_id,
    entityTypeName: event.entity_type_name || undefined,
  };
}

interface ViewStateEventsResponse {
  events: ViewStateEvent[];
  pagination: { has_more: boolean };
}

async function fetchViewStatePage(
  orgId: number,
  entityId: string,
  limit: number,
  eventTypeNames: readonly string[]
): Promise<ViewStateEventsResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    // Probes only ever read occurred_at + entity_id/entity_type_name from the
    // response, never the event payload. Sending payload=none explicitly means
    // a future change to the backend's default can't silently reintroduce
    // payload fetches/decrypts on this hot path.
    payload: 'none',
  });
  for (const name of eventTypeNames) {
    params.append('event_type_name', name);
  }
  // The associated-entity flag is deliberately omitted here: the backend
  // defaults it to true (apps/crm_events/viewsets/entity_viewsets.py
  // view_state), which is exactly the associated-entity fan-out these probes
  // rely on to find email/meeting/linkedin events that live on associated
  // entities rather than on the record itself.
  const url = `/api/orgs/${orgId}/crm-events/entities/${entityId}/view-state/?${params}`;
  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch view-state: ${response.statusText}`);
  }
  return response.json() as Promise<ViewStateEventsResponse>;
}

function formatEventDate(occurredAt: string): string {
  try {
    return formatDistanceToNow(new Date(occurredAt), { addSuffix: true });
  } catch {
    return occurredAt;
  }
}

/**
 * Resolve the most recent event timestamp for `object`.
 *
 * Resolution strategy: one GET view-state?limit=1 request carrying every
 * resolved event type as a repeated `event_type_name` param (zero, one, or
 * many). The backend merges matches across all requested types and orders by
 * occurred_at desc, so the first (and only) event in the response is already
 * the most recent one across the whole highlight — no fan-out needed. An
 * empty `resolvedTypes` list omits the param entirely, matching any event
 * type.
 *
 * Returns `null` value when no matching event is found — the consuming
 * card renders an informative empty state (e.g. "Never emailed") via
 * `HighlightCardShell.emptyText` rather than a generic em-dash.
 *
 * The resolved event's own entity is returned as `targetEntityId` so the tile
 * can open it. This costs nothing extra: the view-state response already
 * carries `entity_id`/`entity_type_name` per event — the resolver used to
 * read only `occurred_at` and throw the rest away.
 */
export function useHighlightLastEvent(
  orgId: number,
  object: CrmObject,
  definition: Extract<HighlightDefinition, { kind: 'last_event' }>
): LinkedHighlightResult {
  const entityId = object.entity_id;

  // Resolve the effective event type names
  const resolvedTypes: string[] =
    definition.eventTypeNames ??
    (definition.eventGroup !== undefined
      ? (EVENT_GROUP_TO_TYPES[definition.eventGroup] ?? [])
      : []);

  // Stable key including type names for cache isolation
  const typesKey = resolvedTypes.join(',');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['highlight', 'last_event', orgId, entityId, typesKey],
    queryFn: async (): Promise<LastEventMatch | null> => {
      // One request for the whole highlight. A limit=1 multi-type view-state
      // request returns the single most recent event across all requested
      // types (backend merges + orders occurred_at desc), which is exactly
      // the "last of group" each consumer renders. Zero types => no
      // event_type_name params => most recent event of any type.
      const result = await fetchViewStatePage(
        orgId,
        entityId,
        1,
        resolvedTypes
      );
      return toMatch(result.events[0]);
    },
    staleTime: 60_000,
    enabled: !!orgId && !!entityId,
  });

  const match = data ?? null;

  // Only offer navigation to a DIFFERENT record. When the event happens to sit
  // on the record being viewed there is nowhere useful to go, and re-opening
  // the current record would read as a broken link.
  const hasNavigableTarget = !!match?.entityId && match.entityId !== entityId;

  return {
    value: match ? formatEventDate(match.occurredAt) : null,
    isLoading,
    isError,
    targetEntityId: hasNavigableTarget ? match.entityId : null,
    targetEntityTypeName: hasNavigableTarget ? match.entityTypeName : undefined,
  };
}
